import type { ExtraBags } from "@b2bportal/air-baggage-api";
import type { BaggageCharge } from "@b2bportal/air-booking-api";
import type { FiatPrice } from "@b2bportal/air-shopping-api";
import { Product } from "@b2bportal/purchase-api";
import { getObjectKeysAsObject, setContextWithKey } from "@checkout/helpers";
import {
  BaggageEventTypes,
  type FlightContext,
  FlightSelectors,
} from "@checkout/states";
import type { BaggageProduct } from "@checkout/states/Products/Flight/states/Baggage/context";
import { trackCheckoutEvent } from "@checkout/tracking/sink";
import { ParentState } from "@checkout/types";
import { BaggageTrackingEvents } from "@hopper-b2b/types";
import { assign } from "xstate";

import type { BaggageEvents, SelectBaggage } from "./events";
import {
  baseBagsTrackingProperties,
  confirmedExtaBagsTrackingProperties,
} from "./selectors";

export const actions = {
  setCheckedBagsPrices: assign((context: FlightContext, _event) => {
    const flightProduct = context[
      ParentState.cartQuote
    ].quoteBreakdown?.products.find((p) => p.product.type === Product.Flight);
    const extraBaggage =
      flightProduct?.product?.value?.pricingSummary?.extraBaggage;
    const chargedCheckedBags = extraBaggage?.checkedBag?.charges;
    const checkedBagsPrices =
      chargedCheckedBags
        ?.sort(
          (a: BaggageCharge, b: BaggageCharge) => a.firstPiece - b.firstPiece
        )
        .flatMap((charge: BaggageCharge) => {
          const bagsCharges: Omit<FiatPrice, "currencySymbol">[] = [];
          for (let i = charge.firstPiece; i <= charge.lastPiece; i++) {
            bagsCharges.push({
              currencyCode: charge.currency,
              value: charge.amount,
            });
          }
          return bagsCharges;
        }) ?? [];

    setContextWithKey(
      context,
      `${ParentState.bagsSelection}.checkedBagsPrices`,
      checkedBagsPrices
    );
    return context;
  }),

  selectBaggage: assign(
    (
      context: FlightContext,
      { passengerId, numCheckedBags }: SelectBaggage
    ) => {
      const passengerBagsMap: Map<string, ExtraBags> =
        context[ParentState.bagsSelection].passengerBagsMap;

      // Does not support extra carryOnBags or personalItems at the moment.
      passengerBagsMap.set(passengerId, {
        checkedBags: numCheckedBags,
        carryOnBags: 0,
        personalItems: 0,
      });

      const shoppedTripId = FlightSelectors.getQuotedShoppedTripId({
        context,
      });
      const selectedFareId = FlightSelectors.getSelectedFareId({
        context,
      });
      const productsToAdd = context[ParentState.cartUpdate].addProducts;
      const filterOutBagsFromProducts =
        productsToAdd?.filter((p) => p.type !== Product.AirBaggage) || [];

      context[ParentState.bagsSelection].passengerBagsMap = passengerBagsMap;

      // Calculate total bags across all passengers
      let totalBags = 0;
      for (const bags of passengerBagsMap.values()) {
        totalBags += bags.checkedBags + bags.carryOnBags + bags.personalItems;
      }

      // Only add the bag product if there are actually bags selected
      if (totalBags > 0) {
        const newBagProduct: BaggageProduct = {
          type: Product.AirBaggage,
          value: {
            tripId: shoppedTripId ?? "",
            fareId: selectedFareId,
            selection: {
              ExtraBagSelection: "PerPassengerExtraBagSelection",
              bagsPerPassenger: Object.fromEntries(passengerBagsMap),
            },
          },
        };
        filterOutBagsFromProducts.push(newBagProduct);
      }

      context[ParentState.cartUpdate].addProducts = filterOutBagsFromProducts;

      // Track that bags was added or removed
      const trackingProperties = baseBagsTrackingProperties(context);
      trackCheckoutEvent({
        eventName: BaggageTrackingEvents.tapped_checked_bag_selection,
        properties: trackingProperties,
      });
      return context;
    }
  ),

  clearBaggageError: assign((context: FlightContext, _event) =>
    setContextWithKey(context, `${ParentState.bagsSelection}.error`, undefined)
  ),

  initSelectedBaggage: assign((context: FlightContext, _event) => {
    if (context[ParentState.bagsSelection].passengerBagsMap == null) {
      setContextWithKey(context, ParentState.bagsSelection, {
        ...context[ParentState.bagsSelection],
        passengerBagsMap: new Map(),
      });
    }
    return context;
  }),

  resetSelectedBaggage: assign((context: FlightContext, _event) => {
    setContextWithKey(context, ParentState.bagsSelection, {
      ...context[ParentState.bagsSelection],
      passengerBagsMap: new Map(),
    });

    // Also remove baggage from cart products to ensure cart state is in sync
    context[ParentState.cartUpdate].addProducts =
      context[ParentState.cartUpdate].addProducts?.filter(
        (product) => product.type !== Product.AirBaggage
      ) || [];

    return context;
  }),

  resetEditBaggageFromReview: assign(
    (context: FlightContext, event: BaggageEvents) => {
      if (event.type === BaggageEventTypes.NEXT) {
        const trackingProperties = confirmedExtaBagsTrackingProperties(context);
        trackCheckoutEvent({
          eventName: BaggageTrackingEvents.confirmed_extra_bags_selection,
          properties: trackingProperties,
        });
      }
      return setContextWithKey(
        context,
        `${ParentState.review}.editBaggageFromReview`,
        false
      );
    }
  ),

  setBaggageVisited: assign((context: FlightContext, _event) =>
    setContextWithKey(context, `${ParentState.bagsSelection}.visited`, true)
  ),

  setSkipBaggageSelection: assign((context: FlightContext, _event) =>
    setContextWithKey(
      context,
      `${ParentState.seatSelection}.skipBaggageSelection`,
      true
    )
  ),

  removeBaggageFromCartUpdate: assign((context: FlightContext, _event) => {
    context[ParentState.cartUpdate].addProducts = context[
      ParentState.cartUpdate
    ].addProducts.filter((product) => product.type !== Product.AirBaggage);
    return context;
  }),

  trackViewedBaggagePageEvent: assign((context: FlightContext, _event) => {
    const trackingProperties = baseBagsTrackingProperties(context);
    trackCheckoutEvent({
      eventName: BaggageTrackingEvents.viewed_extra_bags_screen,
      properties: trackingProperties,
    });
    return context;
  }),
};

export const ActionTypes = getObjectKeysAsObject(actions);
